import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import { PROJECT_SECTIONS, type ProjectSection } from '@/entities/project';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { useProjectDetailTabs } from './useProjectDetailTabs';

/**
 * Horizontal section nav for a project, replacing the left side panel.
 *
 * Renders as its own card between the project header and the section content —
 * three siblings on the page frame rather than a nav nested inside the content,
 * which is the shape `SessionContentLayout` established.
 */
export function ProjectDetailTabs() {
  const { t } = useTranslation('projects');
  const { activeSection, handleSectionClick } = useProjectDetailTabs();

  // Each gated section carries its own flag, so adding a section never means
  // editing this view. Mirrors the side panel it replaces: hiding the entry is
  // the whole gate, since media-api 404s the section's routes while its server
  // flag is off.
  const flags = {
    media_delivery: useBooleanFlagValue('media_delivery', false),
    media_performance: useBooleanFlagValue('media_performance', false),
  };
  // The Approval entry IS the approvals surface, so it goes with the rest of it
  // rather than carrying a flag of its own — `hide_approvals` reads inverted,
  // which the positive `flag` field above cannot express. Mirrors the redirect
  // wired on the same route in `AppRoutes`.
  const assetApprovalsHidden = useAssetApprovalsHidden();

  const sections: ProjectSection[] = PROJECT_SECTIONS.filter((section) => {
    if (section.id === ROUTE_PATTERNS.PROJECT_SECTION_FOLDERS && assetApprovalsHidden) return false;
    return !section.flag || flags[section.flag];
  });

  return (
    <nav
      aria-label={t('projectDetail.projectDetailTabs.projectSections')}
      data-testid="project-detail-tabs"
      className="flex h-[60px] shrink-0 items-center gap-xs rounded-lg bg-card px-md"
    >
      {sections.map((section) => {
        const isActive = activeSection?.id === section.id;

        return (
          <button
            key={section.id}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            disabled={section.disabled}
            data-testid={`project-tab-${section.id}`}
            onClick={() => handleSectionClick(section.id)}
            // The underline is drawn on every tab and only coloured on the
            // active one, so switching tabs does not change the label's
            // baseline by the two pixels a conditional border would add.
            className={`type-label-md flex h-full items-center gap-xs border-b-2 px-sm transition-colors duration-fast ease-out disabled:pointer-events-none disabled:opacity-50 ${
              isActive
                ? 'border-foreground font-medium text-foreground'
                : 'border-transparent font-normal text-muted-foreground hover:text-foreground'
            }`}
          >
            {section.icon}
            {section.label}
          </button>
        );
      })}
    </nav>
  );
}
